import { addDays, format, formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  Loader2,
  Pause,
  Play,
  Square,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdAutoMode } from 'react-icons/md';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAutoChase } from '@/hooks/useAutoChase';
import { cn } from '@/lib/utils';
import { useGetFirmQuery } from '@/services/api/firmsService';
import { useGetChaseSchedulePreviewQuery } from '@/services/api/intakeChaseService';
import type { ChaseScheduleDayPreview } from '@/services/types/intake-chase-types';
import {
  IntakeChaseMessageStatus,
  IntakeChaseStatus,
} from '@/services/types/intake-chase-types';

import { AutoChaseTimeline } from './AutoChaseTimeline';

interface AutoChaseCardProps {
  intakeId: string;
  firmId: string;
  clientName?: string;
  clientPhone?: string;
  intakerName?: string;
}

type ConfirmAction = 'start' | 'pause' | 'resume' | 'stop' | null;

const LOADING_STEPS = [
  'Validating SMS capabilities...',
  'Computing message schedule...',
  'Starting workflow...',
];

/** Cycles through loading step labels with a bottom-to-top slide animation. */
const AnimatedLoadingText: React.FC = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setStepIndex((prev) =>
        prev < LOADING_STEPS.length - 1 ? prev + 1 : prev,
      );
    }, 800);
    return () => clearInterval(intervalRef.current);
  }, []);

  return (
    <span className="relative inline-flex h-4 overflow-hidden items-center">
      {LOADING_STEPS.map((text, i) => (
        <span
          key={text}
          className="absolute left-0 whitespace-nowrap transition-all duration-300 ease-out"
          style={{
            transform: `translateY(${(i - stepIndex) * 100}%)`,
            opacity: i === stepIndex ? 1 : 0,
          }}
        >
          {text}
        </span>
      ))}
    </span>
  );
};

/** Empty fallback used while the schedule preview API is loading. */
const EMPTY_SCHEDULE: ChaseScheduleDayPreview[] = [];


/** Renders message text with template variables filled in and styled with dashed underlines. */
const TemplatedText: React.FC<{ text: string; values: Record<string, string> }> = ({ text, values }) => {
  // Split on {variable} patterns
  const parts = text.split(/(\{[a-z_]+\})/g);
  return (
    <span>
      {parts.map((part, i) => {
        const match = part.match(/^\{([a-z_]+)\}$/);
        if (match) {
          const val = values[match[1]] || match[1];
          return (
            <span
              key={i}
              className="border-b border-dashed border-zinc-500 text-zinc-300"
            >
              {val}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
};

/** Vertical timeline preview of the chase schedule with staggered animations. */
const ScheduleTimelinePreview: React.FC<{
  schedule: ChaseScheduleDayPreview[];
  isLoading?: boolean;
  pcTimezone?: string;
  clientName?: string;
  firmName?: string;
  senderName?: string | null;
  startDay?: number;
}> = ({ schedule, isLoading, pcTimezone, clientName, firmName, senderName, startDay = 1 }) => {
  const [expandedMsg, setExpandedMsg] = useState<string | null>(null);
  const today = useMemo(() => new Date(), []);
  const filteredSchedule = useMemo(
    () => schedule.filter(d => d.day >= startDay),
    [schedule, startDay],
  );
  const filteredMessageCount = useMemo(
    () => filteredSchedule.reduce((sum, d) => sum + d.messages.length, 0),
    [filteredSchedule],
  );

  // Auto-expand first message of filtered schedule
  React.useEffect(() => {
    if (filteredSchedule.length > 0) {
      setExpandedMsg(`${filteredSchedule[0].day}-0`);
    }
  }, [filteredSchedule]);

  const templateValues = useMemo(() => ({
    first_name: clientName?.split(' ')[0] || 'Client',
    firm_name: firmName || 'Your Firm',
    agent_name: senderName || 'your legal team',
    phone: '',
  }), [clientName, firmName, senderName]);

  // Flatten messages for global stagger index
  let globalIdx = 0;

  const tzLabel = pcTimezone
    ? pcTimezone.replace(/_/g, ' ').replace(/^.*\//, '')
    : null;

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="px-4 pt-4 pb-3">
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-wider">
            Schedule Preview
          </span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-3 px-4 pb-8">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
          <span className="text-[11px] font-medium text-zinc-500">Loading schedule...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-wider">
            Schedule Preview
          </span>
          <span className="text-[10px] font-bold text-zinc-600">
            {filteredMessageCount} texts &middot; {filteredSchedule.length} days
          </span>
        </div>
        {tzLabel && (
          <p className="text-[10px] font-medium text-zinc-600">
            Times in client&apos;s timezone ({tzLabel})
          </p>
        )}
      </div>

      {/* Scrollable timeline — hidden scrollbar */}
      <div
        className="flex-1 overflow-y-auto px-4 pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <style>{`
          .chase-timeline-scroll::-webkit-scrollbar { display: none; }
          @keyframes chase-fade-up {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <div className="chase-timeline-scroll">
          {filteredSchedule.map((day, dayIdx) => {
            const dayDate = addDays(today, day.day - startDay);
            const weekday = format(dayDate, 'EEE');
            const dateStr = format(dayDate, 'MMM d');
            const isLast = dayIdx === filteredSchedule.length - 1;

            return (
              <div key={day.day} className="relative">
                {/* Day header */}
                <div
                  className="flex items-center gap-2.5 pb-2"
                  style={{
                    animation: `chase-fade-up 0.4s ease-out ${globalIdx * 30}ms both`,
                  }}
                >
                  {/* Timeline dot — larger for day marker */}
                  <div className="relative z-10 flex items-center justify-center w-5 h-5">
                    <div className="w-2.5 h-2.5 rounded-full bg-zinc-600 ring-2 ring-zinc-900" />
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[11px] font-black text-zinc-300 uppercase tracking-wider">
                      Day {day.day}
                    </span>
                    <span className="text-[10px] font-bold text-zinc-600">
                      {weekday}, {dateStr}
                    </span>
                    <span className="text-[10px] font-medium text-zinc-700">
                      &middot; {day.messages.length} text{day.messages.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {/* Messages */}
                <div className={cn('relative', !isLast && 'pb-3')}>
                  {/* Vertical line */}
                  {!isLast && (
                    <div className="absolute left-[9px] top-0 bottom-0 w-px bg-gradient-to-b from-zinc-700 to-zinc-800" />
                  )}

                  {day.messages.map((msg, msgIdx) => {
                    const staggerIdx = globalIdx;
                    globalIdx += 1;
                    const msgKey = `${day.day}-${msgIdx}`;
                    const isExpanded = expandedMsg === msgKey;

                    return (
                      <div
                        key={msgIdx}
                        className="relative pl-5 mb-1"
                        style={{
                          animation: `chase-fade-up 0.4s ease-out ${staggerIdx * 30 + 60}ms both`,
                        }}
                      >
                        {/* Small dot on the line */}
                        <div className="absolute left-[7px] top-[9px] w-1.5 h-1.5 rounded-full bg-zinc-700" />

                        <button
                          type="button"
                          onClick={() => setExpandedMsg(isExpanded ? null : msgKey)}
                          className={cn(
                            'w-full text-left rounded-lg px-2.5 py-1.5 transition-all duration-200',
                            isExpanded
                              ? 'bg-zinc-800 ring-1 ring-zinc-700'
                              : 'hover:bg-zinc-800/60',
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-bold text-zinc-400">
                              {msg.time}
                            </span>
                            <svg
                              className={cn(
                                'w-3 h-3 text-zinc-600 transition-transform duration-200',
                                isExpanded && 'rotate-180',
                              )}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2.5}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                          {isExpanded && (
                            <p className="mt-1.5 text-[11px] text-zinc-400 font-medium leading-relaxed">
                              <TemplatedText text={msg.text} values={templateValues} />
                            </p>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* Status badge config for consistent styling */
const statusConfig: Record<string, { label: string; className: string }> = {
  [IntakeChaseStatus.ACTIVE]: {
    label: 'Active',
    className: 'bg-emerald-500 text-white',
  },
  [IntakeChaseStatus.NEEDS_ATTENTION]: {
    label: 'Responded',
    className: 'bg-amber-400 text-zinc-900',
  },
  [IntakeChaseStatus.PAUSED]: {
    label: 'Paused',
    className: 'bg-zinc-700 text-zinc-200',
  },
  [IntakeChaseStatus.COMPLETED]: {
    label: 'Completed',
    className: 'bg-emerald-500 text-white',
  },
  [IntakeChaseStatus.STOPPED]: {
    label: 'Stopped',
    className: 'bg-zinc-700 text-zinc-200',
  },
};

export const AutoChaseCard: React.FC<AutoChaseCardProps> = ({
  intakeId,
  firmId,
  clientName,
  clientPhone,
  intakerName,
}) => {
  const {
    chase,
    isLoading,
    startChase,
    updateStatus,
    isStarting,
    isUpdating,
    currentDay,
    currentChase,
    totalMessagesSent,
    totalMessages,
    progressPercent,
    nextMessage,
    canStart,
    canPause,
    canResume,
    canStop,
  } = useAutoChase(intakeId);

  const { data: firm } = useGetFirmQuery(firmId, { skip: !firmId });
  const { data: schedulePreview, isLoading: isScheduleLoading } = useGetChaseSchedulePreviewQuery();
  const scheduleDays = schedulePreview?.days ?? EMPTY_SCHEDULE;
  const hasSmsNumber = Boolean(firm?.sms_sender_number);
  const hasClientPhone = Boolean(clientPhone?.trim());

  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [selectedStartDay, setSelectedStartDay] = useState(1);

  // Remaining messages count
  const remainingMessages = useMemo(() => {
    if (!chase?.messages) return 0;
    return chase.messages.filter(
      (m) =>
        m.status === IntakeChaseMessageStatus.PENDING ||
        m.status === IntakeChaseMessageStatus.SENDING,
    ).length;
  }, [chase?.messages]);

  // Countdown to next message — re-compute every 30s
  const [nextMessageCountdown, setNextMessageCountdown] = useState<string | null>(null);
  useEffect(() => {
    if (!nextMessage?.scheduled_at) {
      setNextMessageCountdown(null);
      return undefined;
    }
    const compute = () => {
      try {
        setNextMessageCountdown(
          formatDistanceToNow(new Date(nextMessage.scheduled_at), { addSuffix: false }),
        );
      } catch {
        setNextMessageCountdown(null);
      }
    };
    compute();
    const interval = setInterval(compute, 30_000);
    return () => clearInterval(interval);
  }, [nextMessage?.scheduled_at]);

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    try {
      switch (confirmAction) {
        case 'start':
          await startChase({ intakeId, start_day: selectedStartDay }).unwrap();
          toast.success('Auto chase started');
          setSelectedStartDay(1);
          break;
        case 'pause':
          await updateStatus({
            intakeId,
            body: { status: 'paused' },
          }).unwrap();
          toast.success('Auto chase paused');
          break;
        case 'resume':
          await updateStatus({
            intakeId,
            body: { status: 'active' },
          }).unwrap();
          toast.success('Auto chase resumed');
          break;
        case 'stop':
          await updateStatus({
            intakeId,
            body: { status: 'stopped' },
          }).unwrap();
          toast.success('Auto chase stopped');
          break;
        default:
          break;
      }
    } catch (err) {
      const apiError = (err as { data?: { error?: string } })?.data?.error;
      toast.error(apiError || `Failed to ${confirmAction} auto chase`);
    } finally {
      setConfirmAction(null);
    }
  };

  // Dialog content based on action
  const getDialogContent = () => {
    switch (confirmAction) {
      case 'start': {
        const phone = clientPhone || chase?.pc_phone || '';
        const filteredSchedule = scheduleDays.filter(d => d.day >= selectedStartDay);
        const filteredMessages = filteredSchedule.reduce((sum, d) => sum + d.messages.length, 0);
        const filteredDays = filteredSchedule.length;
        const countLabel = isScheduleLoading
          ? 'automated texts'
          : `${filteredMessages} automated texts over ${filteredDays} days`;
        return {
          title: `Start Auto Chase for ${clientName || 'this client'}?`,
          description: phone
            ? `This will send ${countLabel} to:`
            : `This will send ${countLabel}. Calls must still be made manually.`,
          phoneNumber: phone,
          actionLabel: selectedStartDay > 1 ? `Start from Day ${selectedStartDay}` : 'Start Auto Chase',
          variant: 'default' as const,
        };
      }
      case 'pause':
        return {
          title: 'Pause Auto Chase',
          description:
            'Pause Auto Chase? No texts will be sent until you resume.',
          actionLabel: 'Pause',
          variant: 'default' as const,
        };
      case 'resume': {
        const responseNote =
          chase?.status === IntakeChaseStatus.NEEDS_ATTENTION &&
          chase?.client_response
            ? `\n\nClient responded: "${chase.client_response}"\n\n`
            : '';
        return {
          title: 'Resume Auto Chase',
          description: `Resume Auto Chase?${responseNote}Resuming will continue sending scheduled texts. ${remainingMessages} text${remainingMessages !== 1 ? 's' : ''} remaining.`,
          actionLabel: 'Resume',
          variant: 'default' as const,
        };
      }
      case 'stop':
        return {
          title: 'Stop Auto Chase',
          description: `Stop Auto Chase? This will permanently cancel ${remainingMessages} remaining text${remainingMessages !== 1 ? 's' : ''}. This cannot be undone.`,
          actionLabel: 'Stop Chase',
          variant: 'destructive' as const,
        };
      default:
        return {
          title: '',
          description: '',
          actionLabel: '',
          variant: 'default' as const,
        };
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-[20px] border border-zinc-200/80 p-5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-black text-zinc-900 tracking-tight">Auto Chase</span>
        </div>
        <div className="mt-3 flex items-center justify-center py-4">
          <div className="w-5 h-5 border-2 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const status = chase?.status;
  const dialogContent = getDialogContent();
  const badge = status ? statusConfig[status] : null;

  return (
    <TooltipProvider>
      <div className="bg-white rounded-[20px] border border-zinc-200/80 p-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-black text-zinc-900 tracking-tight">
              Auto Chase
            </span>
          </div>
          {badge && (
            <span className={cn(
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider',
              badge.className,
            )}>
              {badge.label}
            </span>
          )}
        </div>

        {/* State A: Not Started (no chase exists) */}
        {!chase && (
          <div>
            <p className="text-xs text-zinc-500 font-medium mb-4">
              Automate text follow-ups for this intake.
            </p>
            {hasSmsNumber && hasClientPhone ? (
              <button
                type="button"
                className="w-full h-10 rounded-xl text-xs font-bold bg-zinc-900 text-white hover:bg-zinc-800 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setConfirmAction('start')}
                disabled={isStarting}
              >
                {isStarting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                    <AnimatedLoadingText />
                  </>
                ) : (
                  <>
                    <MdAutoMode className="h-4 w-4" />
                    Start Auto Chase
                  </>
                )}
              </button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <button
                      type="button"
                      className="w-full h-10 rounded-xl text-xs font-bold bg-zinc-200 text-zinc-500 cursor-not-allowed flex items-center justify-center gap-2"
                      disabled
                    >
                      <MdAutoMode className="h-4 w-4" />
                      Start Auto Chase
                    </button>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-900 border-zinc-700 text-zinc-300">
                  <p className="text-xs font-medium">
                    {!hasSmsNumber
                      ? 'Firm has no SMS sender number configured.'
                      : 'Client phone number is required to start auto chase.'}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}

        {/* State B: Active */}
        {status === IntakeChaseStatus.ACTIVE && !canStart && (
          <div className="space-y-4">
            {/* Progress */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-zinc-900">
                  Day {currentDay} / Chase {currentChase} of {totalMessages}
                </span>
                <span className="text-[10px] font-bold text-zinc-500">
                  {totalMessagesSent}/{totalMessages}
                </span>
              </div>
              <div className="w-full h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Next message */}
            {nextMessage && (
              <div className="bg-zinc-50 rounded-xl px-3.5 py-2.5">
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                  Next text {nextMessageCountdown ? `in ${nextMessageCountdown}` : 'soon'}
                </p>
                <p className="text-xs text-zinc-700 font-medium line-clamp-2">
                  {nextMessage.message_text}
                </p>
              </div>
            )}

            {/* Timeline accordion */}
            <Collapsible open={timelineOpen} onOpenChange={setTimelineOpen}>
              <CollapsibleTrigger className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider hover:text-zinc-700 transition-colors">
                {timelineOpen ? 'Hide' : 'Show'} timeline
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <AutoChaseTimeline
                  messages={chase?.messages || []}
                  pcTimezone={chase?.pc_timezone || 'America/New_York'}
                  defaultExpanded
                />
              </CollapsibleContent>
            </Collapsible>

            {/* Actions */}
            <div className="flex gap-2">
              {canPause && (
                <button
                  type="button"
                  className="flex-1 h-9 rounded-xl text-xs font-bold bg-zinc-100 text-zinc-700 hover:bg-zinc-200 active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                  onClick={() => setConfirmAction('pause')}
                  disabled={isUpdating}
                >
                  <Pause className="h-3.5 w-3.5" />
                  Pause
                </button>
              )}
              {canStop && (
                <button
                  type="button"
                  className="flex-1 h-9 rounded-xl text-xs font-bold bg-white text-rose-600 border border-rose-200 hover:bg-rose-50 active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                  onClick={() => setConfirmAction('stop')}
                  disabled={isUpdating}
                >
                  <Square className="h-3.5 w-3.5" />
                  Stop
                </button>
              )}
            </div>
          </div>
        )}

        {/* State C: Needs Attention */}
        {status === IntakeChaseStatus.NEEDS_ATTENTION && !canStart && (
          <div className="space-y-4">
            {/* Response box */}
            {chase?.client_response && (
              <div className="bg-amber-50 rounded-xl px-3.5 py-2.5">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] font-black text-amber-700 uppercase tracking-wider">
                      Client responded
                    </p>
                    <p className="text-xs text-amber-800 font-medium mt-1">
                      &ldquo;{chase.client_response}&rdquo;
                    </p>
                    {chase.client_response_at && (
                      <p className="text-[10px] text-amber-500 font-medium mt-1">
                        {formatDistanceToNow(
                          new Date(chase.client_response_at),
                          { addSuffix: true },
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-zinc-500 font-medium">
              Auto chase is paused. Review the response and decide next steps.
            </p>

            {/* Actions */}
            <div className="flex gap-2">
              {canResume && (
                <button
                  type="button"
                  className="flex-1 h-9 rounded-xl text-xs font-bold bg-zinc-900 text-white hover:bg-zinc-800 active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                  onClick={() => setConfirmAction('resume')}
                  disabled={isUpdating}
                >
                  <Play className="h-3.5 w-3.5" />
                  Resume Chase
                </button>
              )}
              {canStop && (
                <button
                  type="button"
                  className="flex-1 h-9 rounded-xl text-xs font-bold bg-white text-rose-600 border border-rose-200 hover:bg-rose-50 active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                  onClick={() => setConfirmAction('stop')}
                  disabled={isUpdating}
                >
                  <Square className="h-3.5 w-3.5" />
                  Stop
                </button>
              )}
            </div>

            {/* Collapsed timeline */}
            <Collapsible open={timelineOpen} onOpenChange={setTimelineOpen}>
              <CollapsibleTrigger className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider hover:text-zinc-700 transition-colors">
                {timelineOpen ? 'Hide' : 'Show'} timeline
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <AutoChaseTimeline
                  messages={chase?.messages || []}
                  pcTimezone={chase?.pc_timezone || 'America/New_York'}
                />
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* State D: Paused */}
        {status === IntakeChaseStatus.PAUSED && !canStart && (
          <div className="space-y-4">
            <p className="text-xs text-zinc-500 font-medium">
              Paused at Day {currentDay} / Chase {currentChase}. No texts are
              being sent.
            </p>

            {/* Actions */}
            <div className="flex gap-2">
              {canResume && (
                <button
                  type="button"
                  className="flex-1 h-9 rounded-xl text-xs font-bold bg-zinc-900 text-white hover:bg-zinc-800 active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                  onClick={() => setConfirmAction('resume')}
                  disabled={isUpdating}
                >
                  <Play className="h-3.5 w-3.5" />
                  Resume
                </button>
              )}
              {canStop && (
                <button
                  type="button"
                  className="flex-1 h-9 rounded-xl text-xs font-bold bg-white text-rose-600 border border-rose-200 hover:bg-rose-50 active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                  onClick={() => setConfirmAction('stop')}
                  disabled={isUpdating}
                >
                  <Square className="h-3.5 w-3.5" />
                  Stop
                </button>
              )}
            </div>

            {/* Collapsed timeline */}
            <Collapsible open={timelineOpen} onOpenChange={setTimelineOpen}>
              <CollapsibleTrigger className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider hover:text-zinc-700 transition-colors">
                {timelineOpen ? 'Hide' : 'Show'} timeline
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <AutoChaseTimeline
                  messages={chase?.messages || []}
                  pcTimezone={chase?.pc_timezone || 'America/New_York'}
                />
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* State E: Completed/Stopped */}
        {chase &&
          (status === IntakeChaseStatus.COMPLETED ||
            status === IntakeChaseStatus.STOPPED) && (
            <div className="space-y-4">
              <p className="text-xs text-zinc-500 font-medium">
                {status === IntakeChaseStatus.COMPLETED
                  ? `All ${totalMessagesSent} texts sent successfully.`
                  : `Chase stopped after ${totalMessagesSent} of ${totalMessages} texts.`}
              </p>

              {/* Collapsed timeline */}
              {chase.messages && chase.messages.length > 0 && (
                <Collapsible open={timelineOpen} onOpenChange={setTimelineOpen}>
                  <CollapsibleTrigger className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider hover:text-zinc-700 transition-colors">
                    {timelineOpen ? 'Hide' : 'Show'} timeline
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <AutoChaseTimeline
                      messages={chase.messages}
                      pcTimezone={chase.pc_timezone || 'America/New_York'}
                    />
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Restart button */}
              {hasSmsNumber && hasClientPhone ? (
                <button
                  type="button"
                  className="w-full h-9 rounded-xl text-xs font-bold bg-zinc-100 text-zinc-700 hover:bg-zinc-200 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setConfirmAction('start')}
                  disabled={isStarting}
                >
                  {isStarting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                      <AnimatedLoadingText />
                    </>
                  ) : (
                    <>
                      <MdAutoMode className="h-4 w-4" />
                      Start New Chase
                    </>
                  )}
                </button>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <button
                        type="button"
                        className="w-full h-9 rounded-xl text-xs font-bold bg-zinc-200 text-zinc-500 cursor-not-allowed flex items-center justify-center gap-2"
                        disabled
                      >
                        <MdAutoMode className="h-4 w-4" />
                        Start New Chase
                      </button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="bg-zinc-900 border-zinc-700 text-zinc-300">
                    <p className="text-xs font-medium">
                      {!hasSmsNumber
                        ? 'Firm has no SMS sender number configured.'
                        : 'Client phone number is required to start auto chase.'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )}

        {/* Confirmation Dialog — dark theme matching End Intake style */}
        <AlertDialog
          open={confirmAction !== null}
          onOpenChange={(open) => {
            if (!open) {
              setConfirmAction(null);
              setSelectedStartDay(1);
            }
          }}
        >
          <AlertDialogContent
            className={cn(
              'bg-zinc-900 border-zinc-800 rounded-2xl p-0 gap-0 overflow-hidden',
              confirmAction === 'start' ? 'max-w-2xl' : 'max-w-sm',
            )}
          >
            {confirmAction === 'start' ? (
              /* Two-column layout for Start dialog */
              <div className="flex min-h-0">
                {/* Left: Schedule timeline preview */}
                <div className="w-[320px] shrink-0 border-r border-zinc-800 flex flex-col max-h-[460px]">
                  <ScheduleTimelinePreview
                    schedule={scheduleDays}
                    isLoading={isScheduleLoading}
                    pcTimezone={chase?.pc_timezone}
                    clientName={clientName}
                    firmName={firm?.full_name || firm?.name}
                    senderName={intakerName || firm?.sms_sender_name}
                    startDay={selectedStartDay}
                  />
                </div>

                {/* Right: Confirmation content */}
                <div className="flex-1 flex flex-col">
                  <AlertDialogHeader className="px-5 pt-5 pb-3 space-y-2">
                    <AlertDialogTitle className="text-xs font-black text-white uppercase tracking-wider">
                      {dialogContent.title}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-sm text-zinc-400 font-medium whitespace-pre-wrap leading-relaxed">
                      {dialogContent.description}
                    </AlertDialogDescription>
                    {'phoneNumber' in dialogContent && dialogContent.phoneNumber ? (
                      <div className="mt-2 space-y-2">
                        <div className="bg-zinc-800 rounded-xl px-3.5 py-2.5 flex items-center gap-2.5">
                          <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">SMS</span>
                          <span className="text-sm font-bold text-white tracking-wide">{dialogContent.phoneNumber}</span>
                        </div>
                        <p className="text-xs text-zinc-500 font-medium">Calls must still be made manually.</p>
                      </div>
                    ) : null}

                    {/* Start Day Selector */}
                    <div className="mt-3 space-y-1.5">
                      <span className="text-[10px] font-black text-zinc-500 uppercase tracking-wider">
                        Start Point
                      </span>
                      <Select
                        value={String(selectedStartDay)}
                        onValueChange={(val) => setSelectedStartDay(Number(val))}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-200 text-xs font-bold h-9 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {scheduleDays.map((day) => (
                            <SelectItem
                              key={day.day}
                              value={String(day.day)}
                              className="text-xs font-medium text-zinc-300 focus:bg-zinc-700 focus:text-white"
                            >
                              Day {day.day}{day.day === 1 ? ' (default)' : ''} — {day.messages.length} text{day.messages.length !== 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedStartDay > 1 && (
                        <p className="text-[10px] text-zinc-500 font-medium">
                          Skipping days 1–{selectedStartDay - 1}. Chase will begin at Day {selectedStartDay}.
                        </p>
                      )}
                    </div>
                  </AlertDialogHeader>
                  <div className="mt-auto p-4 pt-2 space-y-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        handleConfirmAction();
                      }}
                      disabled={isStarting}
                      className="w-full h-10 rounded-xl text-xs font-bold bg-zinc-100 text-zinc-900 hover:bg-white disabled:opacity-50 transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                    >
                      {isStarting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                          <AnimatedLoadingText />
                        </>
                      ) : (
                        <>
                          <MdAutoMode className="h-4 w-4" />
                          {dialogContent.actionLabel}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmAction(null)}
                      disabled={isStarting}
                      className="w-full h-10 rounded-xl bg-transparent text-zinc-500 text-xs font-bold hover:text-zinc-300 transition-all disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Single-column layout for other actions */
              <>
                <AlertDialogHeader className="px-5 pt-5 pb-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <MdAutoMode className="h-4 w-4 text-zinc-400" />
                    <AlertDialogTitle className="text-xs font-black text-white uppercase tracking-wider">
                      {dialogContent.title}
                    </AlertDialogTitle>
                  </div>
                  <AlertDialogDescription className="text-sm text-zinc-400 font-medium whitespace-pre-wrap leading-relaxed">
                    {dialogContent.description}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="p-4 pt-2 space-y-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleConfirmAction();
                    }}
                    disabled={isStarting || isUpdating}
                    className={cn(
                      'w-full h-10 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 active:scale-[0.98]',
                      dialogContent.variant === 'destructive'
                        ? 'bg-rose-500 text-white hover:bg-rose-400 disabled:opacity-50'
                        : 'bg-zinc-100 text-zinc-900 hover:bg-white disabled:opacity-50',
                    )}
                  >
                    {(isStarting || isUpdating) && (
                      <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                    )}
                    {dialogContent.actionLabel}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmAction(null)}
                    disabled={isStarting || isUpdating}
                    className="w-full h-10 rounded-xl bg-transparent text-zinc-500 text-xs font-bold hover:text-zinc-300 transition-all disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};
